import "./phone-dispatch-modal.css";

import type { InputChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Input } from "@tql/ui-components-react/kendo/inputs";
import { Error } from "@tql/ui-components-react/kendo/labels";
import { useEffect, useId, useState } from "react";

import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";

import { usePostPhoneDispatch } from "./hooks/use-post-phone-dispatch.ts";

export const PhoneDispatchModal = ({
	onClose,
	isOpen,
	poNumber,
	customerId,
}: {
	onClose: () => void;
	isOpen: boolean;
	poNumber: string;
	customerId?: number | null;
}) => {
	const confirmationNumberId = useId();
	const errorId = `${confirmationNumberId}_error`;
	const [confirmationNumber, setConfirmationNumber] = useState("");
	const [showError, setShowError] = useState(false);

	const { postPhoneDispatchAsync, isPostingPhoneDispatch } =
		usePostPhoneDispatch();

	// Match the 15s auto-close used by toast error notifications elsewhere.
	useEffect(() => {
		if (!showError) return;
		const timer = setTimeout(() => setShowError(false), 15_000);
		return () => clearTimeout(timer);
	}, [showError]);

	const handleChange = (event: InputChangeEvent) => {
		if (showError) {
			setShowError(false);
		}
		setConfirmationNumber(String(event.target.value ?? ""));
	};

	const handleConfirm = async () => {
		if (!confirmationNumber.trim()) {
			setShowError(true);
			return;
		}
		try {
			await postPhoneDispatchAsync({
				confirmationNumber,
				poNumber,
				customerId,
			});
			onClose();
		} catch {
			// handle error if needed
		}
	};

	if (!isOpen) {
		return null;
	}

	return (
		<TraxModal
			cancelText={"Don't Confirm"}
			confirmDisabled={isPostingPhoneDispatch}
			confirmText={"Confirm Dispatch"}
			onCancel={onClose}
			onClose={onClose}
			onConfirm={() => void handleConfirm()}
			title={`Phone Dispatch`}
			type={"confirmation"}
			width={313}
		>
			<LoadingSpinner isVisible={isPostingPhoneDispatch} />
			<div className={"phone-dispatch-field"}>
				<label htmlFor={confirmationNumberId}>
					Please enter the Confirmation # to proceed.
					<span className={"required"}>*</span>
				</label>
				<Input
					ariaDescribedBy={showError ? errorId : undefined}
					id={confirmationNumberId}
					onChange={handleChange}
					placeholder={"00000000"}
					valid={!showError}
					value={confirmationNumber}
				/>
				{showError ? (
					<Error id={errorId}>Confirmation # is required.</Error>
				) : null}
			</div>
		</TraxModal>
	);
};
